// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/pages/PrivacyPolicy.tsx
// Version: v2.1.0
// Description: Privacy Policy page — real ESTATA content, Partners-style layout

import React from "react";
import { useBreakpoints } from "../hooks/Breakpoints.tsx";
import Container from "../hooks/Container.tsx";

const PrivacyPolicy: React.FC = () => {
  const { current } = useBreakpoints();
  const isDesktop = current === "desktop";
  const isTablet = current === "tablet";

  const titleSize = isDesktop ? "text-7xl" : isTablet ? "text-5xl" : "text-3xl";
  const h2Size = isDesktop ? "text-3xl" : isTablet ? "text-2xl" : "text-xl";
  const h3Size = isDesktop ? "text-xl" : isTablet ? "text-lg" : "text-base";
  const bodySize = isDesktop ? "text-lg" : isTablet ? "text-base" : "text-sm";

  return (
    <section className="bg-black text-white pt-[50px] pb-[50px] sm:pt-[75px] sm:pb-[75px] lg:pt-[100px] lg:pb-[100px]">
      <Container>
        <h1 className={`font-bold mb-8 ${titleSize}`}>Datenschutzerklärung</h1>

        <p className={`${bodySize} font-semibold mb-4 text-white/90`}>
          Der Schutz Ihrer Daten gehört zu den wichtigsten Grundsätzen der ESTATA GmbH.
        </p>
        <p className={`${bodySize} font-semibold mb-4 text-white/90`}>
          Wir möchten Sie mit dieser Datenschutzerklärung über Art, Umfang und Zweck der von uns erhobenen,
          genutzten und verarbeiteten personenbezogenen Daten informieren. Ferner möchten wir Sie über die
          Ihnen zustehenden Rechte aufklären.
        </p>
        <p className={`${bodySize} mb-2 text-white/60`}>
          Bitte beachten Sie, dass diese Datenschutzerklärung regelmäßig aktualisiert wird, um sie an die
          Veränderungen anzupassen, die sich hinsichtlich unserer Behandlung Ihrer personenbezogenen Daten
          oder der geltenden Rechtslage ergeben haben.
        </p>
        <p className={`${bodySize} mb-12 text-white/40`}>(Stand: 12.10.2022, Version 1.14)</p>

        {/* 1. Datenschutz auf einen Blick */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>1. Datenschutz auf einen Blick</h2>
          <div className="h-px bg-white/20 mb-6" />

          <h3 className={`${h3Size} font-semibold mb-3`}>Allgemeine Hinweise</h3>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten
            passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich
            identifiziert werden können.
          </p>

          <h3 className={`${h3Size} font-semibold mb-3`}>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h3>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können
            Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
          </p>

          <h3 className={`${h3Size} font-semibold mb-3`}>Wie erfassen wir Ihre Daten?</h3>
          <p className={`${bodySize} text-white/80 mb-4`}>
            Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um
            Daten handeln, die Sie in ein Kontaktformular eingeben.
          </p>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere
            IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder
            Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.
          </p>

          <h3 className={`${h3Size} font-semibold mb-3`}>Welche Rechte haben Sie bezüglich Ihrer Daten?</h3>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten
            personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser
            Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese
            Einwilligung jederzeit für die Zukunft widerrufen.
          </p>
        </div>

        {/* 2. Hosting und CDN */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>2. Hosting und Content Delivery Networks (CDN)</h2>
          <div className="h-px bg-white/20 mb-6" />

          <h3 className={`${h3Size} font-semibold mb-3`}>Externes Hosting</h3>
          <p className={`${bodySize} text-white/80 mb-4`}>
            Diese Website wird bei einem externen Dienstleister gehostet (Hoster). Die personenbezogenen Daten,
            die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert.
          </p>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Wir setzen folgenden Hoster ein:<br />
            STRATO AG<br />
            Otto-Ostrowski-Straße 7<br />
            10249 Berlin
          </p>
        </div>

        {/* 3. Allgemeine Hinweise und Pflichtinformationen */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>3. Allgemeine Hinweise und Pflichtinformationen</h2>
          <div className="h-px bg-white/20 mb-6" />

          <h3 className={`${h3Size} font-semibold mb-3`}>Hinweis zur verantwortlichen Stelle</h3>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:<br /><br />
            ESTATA GmbH<br />
            Katharinenstraße 9<br />
            10711 Berlin<br /><br />
            Direct: +49 30 6298 7730<br />
            Mail: info@estata.com
          </p>

          <h3 className={`${h3Size} font-semibold mb-3`}>Gesetzlich vorgeschriebener Datenschutzbeauftragter</h3>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Wir haben für unser Unternehmen einen Datenschutzbeauftragten bestellt.<br /><br />
            ESTATA GmbH<br />
            Ines Gebhardt<br />
            Katharinenstraße 9<br />
            10711 Berlin<br /><br />
            Direct: +49 30 6298 7730<br />
            Mail: info@estata.com
          </p>

          <h3 className={`${h3Size} font-semibold mb-3`}>Widerspruchsrecht (Art. 21 DSGVO)</h3>
          <p className={`${bodySize} text-white/80 mb-6 uppercase font-medium`}>
            Wenn die Datenverarbeitung auf Grundlage von Art. 6 Abs. 1 lit. e oder f DSGVO erfolgt, haben Sie
            jederzeit das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, gegen die Verarbeitung
            Ihrer personenbezogenen Daten Widerspruch einzulegen.
          </p>

          <h3 className={`${h3Size} font-semibold mb-3`}>SSL- bzw. TLS-Verschlüsselung</h3>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine
            SSL- bzw. TLS-Verschlüsselung.
          </p>

          <h3 className={`${h3Size} font-semibold mb-3`}>Recht auf Einschränkung der Verarbeitung</h3>
          <p className={`${bodySize} text-white/80 mb-4`}>
            Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
          </p>
          <ul className={`${bodySize} text-white/80 mb-6 list-disc pl-6 space-y-2`}>
            <li>Wenn Sie die Richtigkeit Ihrer bei uns gespeicherten personenbezogenen Daten bestreiten.</li>
            <li>Wenn die Verarbeitung Ihrer personenbezogenen Daten unrechtmäßig geschah/geschieht.</li>
            <li>Wenn wir Ihre personenbezogenen Daten nicht mehr benötigen, Sie sie jedoch zur Geltendmachung von Rechtsansprüchen benötigen.</li>
            <li>Wenn Sie einen Widerspruch nach Art. 21 Abs. 1 DSGVO eingelegt haben.</li>
          </ul>
        </div>

        {/* 4. Datenerfassung */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>4. Datenerfassung auf dieser Website</h2>
          <div className="h-px bg-white/20 mb-6" />
          <h3 className={`${h3Size} font-semibold mb-3`}>Server-Log-Dateien</h3>
          <ul className={`${bodySize} text-white/80 mb-4 list-disc pl-6 space-y-1`}>
            <li>Browsertyp und Browserversion</li>
            <li>verwendetes Betriebssystem</li>
            <li>Referrer URL</li>
            <li>Hostname des zugreifenden Rechners</li>
            <li>Uhrzeit der Serveranfrage</li>
            <li>IP-Adresse</li>
          </ul>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
          </p>
        </div>

        {/* 5. Plugins und Tools */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>5. Plugins und Tools</h2>
          <div className="h-px bg-white/20 mb-6" />
          <h3 className={`${h3Size} font-semibold mb-3`}>YouTube mit erweitertem Datenschutz</h3>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Diese Website bindet Videos der YouTube ein. Wir nutzen YouTube im erweiterten Datenschutzmodus.
            Dies stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar.
          </p>
        </div>

        {/* 6. Konferenzen */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>6. Audio- und Videokonferenzen</h2>
          <div className="h-px bg-white/20 mb-6" />
          <h3 className={`${h3Size} font-semibold mb-3`}>Microsoft Teams</h3>
          <p className={`${bodySize} text-white/80 mb-6`}>
            Wir nutzen Microsoft Teams. Anbieter ist die Microsoft Corporation, One Microsoft Way, Redmond,
            WA 98052-6399, USA. Wir haben mit dem Anbieter einen Vertrag zur Auftragsverarbeitung abgeschlossen
            und setzen die strengen Vorgaben der deutschen Datenschutzbehörden vollständig um.
          </p>
        </div>

        <div className="h-px bg-white/20 mb-6" />
        <p className={`${bodySize} text-white/40`}>
          Weitere Informationen zum Datenschutz erhalten Sie unter info@estata.com
        </p>
      </Container>
    </section>
  );
};

export default PrivacyPolicy;
